import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { hasAnyUserRight, UserRight } from './user.model';

/**
 * Guard de droits applicatif (défense en profondeur côté front).
 *
 * - Vérifie d'abord que l'utilisateur est authentifié (sinon redirection vers /login).
 * - Vérifie ensuite qu'il possède **au moins un** des droits attendus.
 * - Si aucun droit n'est satisfait, redirige vers /dashboard plutôt que /login
 *   pour éviter de déconnecter un utilisateur authentifié sans droit suffisant.
 *
 * IMPORTANT : ce guard ne remplace pas le contrôle d'autorisation côté backend,
 * il agit en défense en profondeur pour éviter qu'un utilisateur sans droit
 * n'atteigne une page admin et déclenche des appels API qui échoueraient.
 *
 * Usage dans la table de routes :
 *   { path: 'admin', canActivate: [authGuard, roleGuard(['accesGestionUtilisateurs'])], ... }
 */
export function roleGuard(rights: readonly UserRight[]): CanActivateFn {
  return (_route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      authService.logout();
      return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    }

    const user = authService.getCurrentUser();
    if (hasAnyUserRight(user, rights)) {
      return true;
    }

    return router.createUrlTree(['/dashboard']);
  };
}
